import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Button,
  Chip,
  Paper,
  Alert,
  CircularProgress,
  Checkbox,
  Divider,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../services/api';

interface Case {
  id: number;
  case_name: string;
  case_type: string;
  service_type_id: number;
  service_type?: { name: string; short_code: string };
  status: string;
  creator?: { name: string; email: string };
  firm?: { name: string };
}

interface Slot {
  id: number;
  available_date: string;
  time_slot: string;
  is_booked: boolean;
  booked_by?: {
    name: string;
    email: string;
  };
  booked_at?: string;
  created_at: string;
}

const FirmAdminSlotSelection: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [caseData, setCaseData] = useState<Case | null>(null);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (id) {
      fetchCaseAndSlots();
    }
  }, [id]);

  const fetchCaseAndSlots = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/cases/${id}/slots`);
      setCaseData(res.data.case);
      setSlots(res.data.slots);
    } catch (err: any) {
      setError('Failed to load case and slots');
      toast.error('Failed to load case and slots');
    } finally {
      setLoading(false);
    }
  };

  const handleSlotSelection = (slot: Slot) => {
    if (slot.is_booked) {
      toast.error('This slot is already booked');
      return;
    }
    setSelectedSlot(slot);
  };

  const handleBookSlot = async () => {
    if (!selectedSlot) {
      toast.error('Please select a slot first');
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/cases/${id}/book-slot`, {
        slot_id: selectedSlot.id,
      });

      toast.success('Slot booked successfully!');
      setSelectedSlot(null);
      fetchCaseAndSlots(); // Refresh to show updated status
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to book slot');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const groupSlotsByDate = (slots: Slot[]) => {
    const grouped: { [key: string]: Slot[] } = {};
    slots.forEach(slot => {
      const date = new Date(slot.available_date).toDateString();
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(slot);
    });
    return grouped;
  };

  if (loading) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='200px'
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity='error'>{error}</Alert>
      </Box>
    );
  }

  if (!caseData) {
    return (
      <Box>
        <Alert severity='warning'>Case not found</Alert>
      </Box>
    );
  }

  const availableSlots = slots.filter(slot => !slot.is_booked);
  const groupedSlots = groupSlotsByDate(availableSlots);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Typography variant='h4' sx={{ flexGrow: 1 }}>
          Select Meeting Time
        </Typography>
      </Box>

      {/* Case Info */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title='Case Information'
          subheader={`Case: ${caseData.case_name} (${caseData.service_type?.short_code || 'N/A'})`}
        />
        <CardContent>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2,
            }}
          >
            <Box>
              <Typography variant='body2' color='text.secondary'>
                <strong>Case Type:</strong> {caseData.case_type}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                <strong>Service Type:</strong>{' '}
                {caseData.service_type?.name || 'N/A'}
              </Typography>
            </Box>
            <Box>
              <Typography variant='body2' color='text.secondary'>
                <strong>Firm Admin:</strong> {caseData.creator?.name || 'N/A'}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                <strong>Firm:</strong> {caseData.firm?.name || 'N/A'}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Selected Slot Summary */}
      {selectedSlot && (
        <Card
          sx={{
            mb: 3,
            bgcolor: 'primary.light',
            color: 'primary.contrastText',
          }}
        >
          <CardContent>
            <Box
              display='flex'
              alignItems='center'
              justifyContent='space-between'
            >
              <Box>
                <Typography variant='h6'>Selected Slot</Typography>
                <Typography variant='body1'>
                  {formatDate(selectedSlot.available_date)} at{' '}
                  {selectedSlot.time_slot} EDT
                </Typography>
              </Box>
              <Button
                variant='contained'
                color='secondary'
                startIcon={<SendIcon />}
                onClick={handleBookSlot}
                disabled={submitting}
              >
                {submitting ? 'Booking...' : 'Book This Slot'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Available Slots */}
      {availableSlots.length > 0 ? (
        <Card>
          <CardHeader
            title='Available Meeting Slots'
            subheader={`Found ${availableSlots.length} available slots (All times in EDT - Eastern Daylight Time)`}
          />
          <CardContent>
            {Object.entries(groupedSlots).map(([date, dateSlots]) => (
              <Box key={date} sx={{ mb: 3 }}>
                <Typography variant='h6' sx={{ mb: 2, color: 'primary.main' }}>
                  {formatDate(date)}
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: '1fr 1fr',
                      md: '1fr 1fr 1fr',
                    },
                    gap: 2,
                  }}
                >
                  {dateSlots.map(slot => (
                    <Paper
                      key={slot.id}
                      elevation={selectedSlot?.id === slot.id ? 8 : 2}
                      sx={{
                        p: 2,
                        border: selectedSlot?.id === slot.id ? 2 : 1,
                        borderColor:
                          selectedSlot?.id === slot.id
                            ? 'primary.main'
                            : 'divider',
                        cursor: 'pointer',
                        '&:hover': { elevation: 4 },
                      }}
                      onClick={() => handleSlotSelection(slot)}
                    >
                      <Box
                        display='flex'
                        alignItems='center'
                        justifyContent='space-between'
                      >
                        <Box>
                          <Typography variant='body1' fontWeight='bold'>
                            {slot.time_slot}
                          </Typography>
                          <Typography variant='body2' color='text.secondary'>
                            Available (EDT)
                          </Typography>
                        </Box>
                        <Checkbox
                          checked={selectedSlot?.id === slot.id}
                          color='primary'
                        />
                      </Box>
                    </Paper>
                  ))}
                </Box>
                <Divider sx={{ mt: 2 }} />
              </Box>
            ))}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent>
            <Box textAlign='center' py={4}>
              <CalendarIcon
                sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }}
              />
              <Typography variant='h6' color='text.secondary' gutterBottom>
                No Available Slots
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                All slots have been booked or no slots are available for this
                case.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Booked Slots (for reference) */}
      {slots.filter(slot => slot.is_booked).length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardHeader
            title='Booked Slots'
            subheader='These slots are no longer available'
          />
          <CardContent>
            {slots
              .filter(slot => slot.is_booked)
              .map(slot => (
                <Paper key={slot.id} sx={{ p: 2, mb: 2, bgcolor: 'grey.100' }}>
                  <Box
                    display='flex'
                    alignItems='center'
                    justifyContent='space-between'
                  >
                    <Box>
                      <Typography variant='body1' fontWeight='bold'>
                        {slot.time_slot}
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        {formatDate(slot.available_date)}
                      </Typography>
                      {slot.booked_by && (
                        <Typography variant='body2' color='text.secondary'>
                          Booked by: {slot.booked_by.name}
                        </Typography>
                      )}
                    </Box>
                    <Chip label='Booked' color='error' size='small'  onClick={() => false} />
                  </Box>
                </Paper>
              ))}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default FirmAdminSlotSelection;
